import React, { useCallback } from 'react'
import { useStore } from '@xyflow/react'
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import type { OffscreenDirections } from './utils/canvasOffscreen'
import { useOffscreenState } from './hooks/useOffscreenState'
import { highlightedHint } from './utils/canvasPanDirection'
import { useCanvasPanDirection } from './hooks/useCanvasPanDirection'
import type { CanvasToolbarSide } from './utils/canvasToolbarSide'
import './CanvasOffscreenHints.scss'

/**
 * Einzug der Aktionsgruppe vom rechten Canvas-Rand (`&__actions` in
 * `ProjectCanvasPanel.scss`). Nur für die Ausweich-Rechnung unten.
 *
 * NICHT der Randabstand der Pfeile: Die sitzen bündig an der Kante. Beide
 * Werte waren einmal 8 und sahen deshalb wie derselbe aus — sie sind es nicht.
 * Inzwischen ist die Aktionsgruppe weiter in ihre Ecke gerückt (4px), die
 * Kopfzeile links ebenso; die Zahl hier zieht mit, sonst wiche der obere
 * Kantenpfeil zu früh aus.
 */
const ACTIONS_PLATE_EDGE = 4

/**
 * Die halbe Kantenlänge eines Chevrons, für dieselbe Rechnung.
 *
 * Hängt an `ICON_SIZE` unten — wer dort dreht, dreht hier mit. Es waren 24px,
 * solange der Randpfeil so groß gezeichnet war wie die Bedienelemente.
 */
const HALF_ICON = 10

/**
 * Die Kantenlänge eines Chevrons — 20px, nicht 24.
 *
 * 24 ist das Maß der BEDIENUNG: So groß sind die Icons in der Werkzeug-Pille
 * und in der Aktionsgruppe. Der Randpfeil ist eine Nebenbemerkung und darf
 * darunter liegen; er stand vorher gleichauf mit dem, wovor er zurücktreten
 * soll. Dieselbe Zahl steht als `$hint-size` im Stylesheet.
 *
 * `strokeWidth` aus demselben Grund: 1.5 wie die Werkzeuge in der Pille
 * (`CanvasToolbar.tsx`). Mit lucides Standard 2 war der Randpfeil KRÄFTIGER
 * gestrichelt als die Knöpfe, denen er ausweicht — daher rührte der größte
 * Teil seiner Lautstärke, nicht aus der Farbe.
 */
const ICON_SIZE = 20
const ICON_STROKE = 1.5

export interface CanvasOffscreenHintsProps {
  /**
   * Die gemessene Breite der Aktionsgruppe oben rechts. Ohne sie weicht der
   * obere Pfeil nicht aus — solange nichts gemessen ist, gibt es auch nichts,
   * dem man ausweichen könnte.
   *
   * Gemessen statt fest gesetzt, weil sie am übersetzten Text hängt
   * („Freigeben" / „Share"): Eine Zahl wäre in einer der beiden Sprachen
   * falsch.
   */
  actionsWidth?: number
  /**
   * Ob der LINKE Pfeil weichen soll, weil dort gerade das **Drag Handle**
   * steht.
   *
   * Beide wollen dieselbe Stelle: die Mitte der linken Kante. Solange man die
   * Breite verstellt, ist der Hinweis auf Elemente außerhalb des
   * **Ausschnitts** die unwichtigere Auskunft — er kommt wieder, sobald der
   * Zeiger die Kante verlässt.
   *
   * Er wird dabei AUSGEBLENDET, nicht ausgehängt: Ein Element, das aus dem DOM
   * fällt, kann nicht überblenden — es wäre schlagartig weg, während der Griff
   * daneben sanft erscheint. Deshalb bleibt er stehen und bekommt nur
   * `--suppressed`.
   */
  suppressLeft?: boolean
  /**
   * Auf welcher Seite die **Werkzeug-Pille** steht. Der Pfeil an DIESER Kante
   * weicht ihr aus; der an der anderen sitzt bündig.
   */
  toolbarSide?: CanvasToolbarSide
  /**
   * ALLE Pfeile weichen — solange die **Werkzeug-Pille** am Griff gezogen
   * wird. Das Feld gehört dann den Ableg-Flächen, und ein Pfeil, der neben
   * einer landenden Pille aufleuchtet, wäre die falsche Auskunft zur falschen
   * Zeit. Ausgeblendet wie `suppressLeft`, nicht ausgehängt: Sie faden aus
   * und danach wieder ein.
   */
  suppressAll?: boolean
}

/** Was das Bauteil zum Zeichnen braucht — acht Richtungen plus das Ausweichen. */
interface HintState extends OffscreenDirections {
  /** Ob der obere KANTENpfeil unter die Aktionsgruppe rutschen muss. */
  dodgeActions: boolean
}

/**
 * Die vier **Eckpfeile**, in Zeichenreihenfolge.
 *
 * Als Liste, weil sie sich nichts zu sagen haben: Anders als die Kantenpfeile
 * weicht keiner von ihnen je aus (siehe unten), also unterscheiden sie sich
 * allein in ihrer Lage — und die steckt im Modifier.
 */
const CORNERS = [
  { key: 'topLeft', modifier: 'top-left' },
  { key: 'topRight', modifier: 'top-right' },
  { key: 'bottomLeft', modifier: 'bottom-left' },
  { key: 'bottomRight', modifier: 'bottom-right' },
] as const

/**
 * Die **Randpfeile** — Chevrons am Canvas-Rand, die anzeigen, dass in dieser
 * Richtung **Canvas-Elemente** außerhalb des **Ausschnitts** liegen. Siehe
 * „Randpfeil" in CONTEXT.md.
 *
 * **Auswertung und Anzeige in einem Bauteil**, über EINE Subscription auf
 * React Flows Store. Sie deckt alle vier Auslöser ab, die die Antwort ändern
 * können:
 *
 * | | |
 * |---|---|
 * | Schieben, Zoomen | `transform` |
 * | Notiz ziehen, anlegen, löschen | `nodeLookup` |
 * | Canvas ändert seine Größe (geteilt ↔ maximiert, Fenster) | `width`/`height` |
 *
 * Der letzte wird leicht übersehen: Er ändert weder den Ausschnitt noch die
 * Notizen, aber sehr wohl, was man sieht.
 *
 * Gerendert wird nur, wenn sich am BILD etwas ändert — dafür sorgt
 * `sameHints` als Vergleichsfunktion. Ohne sie liefe ein Rendern je Frame,
 * und genau davor warnt der `getZoom()`-Kommentar in `handleNodesChange`.
 *
 * **Einer von ihnen leuchtet, während gefahren wird** — der in Fahrtrichtung,
 * violett und eine Spur kräftiger. Die Richtung kommt aus
 * `useCanvasPanDirection`, welcher Pfeil sie bekommt aus `highlightedHint`:
 * Nur hier ist beides zugleich bekannt — die Bewegung und die Frage, welche
 * Pfeile überhaupt stehen.
 *
 * Gehört als Kind von `<CanvasFlow>` gerendert, damit es innerhalb von
 * `.react-flow` landet — nur dort greift die Schichtung über den Notizen
 * (ADR 0008).
 *
 * @example
 * <CanvasFlow …>
 *   <CanvasOffscreenHints actionsWidth={gemessen} />
 * </CanvasFlow>
 */
export const CanvasOffscreenHints: React.FC<CanvasOffscreenHintsProps> = ({ actionsWidth, suppressLeft = false, toolbarSide = 'left', suppressAll = false }) => {
  /*
    Die Richtungen kommen aus dem gemeinsamen Hook — dieselbe Schleife, aus der
    auch die **Karte** ihre Antwort bekommt. Zwei eigene Selektoren wären zwei
    Durchgänge über hundert Elemente je Bild, und schlimmer: zwei Wahrheiten
    darüber, was „draußen" heißt.
  */
  const { directions } = useOffscreenState()

  /*
    Das Ausweichen vor der Aktionsgruppe braucht KEINE Schleife, nur die
    Breite der Fläche — deshalb ein eigener, billiger Selektor statt eines
    weiteren Feldes im Hook. Der obere Pfeil sitzt waagerecht mittig; die
    Aktionsgruppe klebt rechts. Sie überschneiden sich erst, wenn der Canvas
    schmal wird — im geteilten Modus darf er bis auf 320px schrumpfen.
  */
  const dodgeActions = useStore(useCallback((state: { width: number }) => (
    actionsWidth !== undefined &&
    state.width / 2 + HALF_ICON > state.width - actionsWidth - ACTIONS_PLATE_EDGE
  ), [actionsWidth]))

  const hints: HintState = { ...directions, dodgeActions }
  const { left, right, top, bottom } = directions


  /*
    Der Pfeil, der gerade leuchtet — höchstens einer.

    Die Bewegung allein genügt dafür nicht: Fährt man schräg, wo nur ein
    Kantenpfeil steht, weicht die Hervorhebung auf ihn aus (`highlightedHint`).
    Diese Entscheidung braucht beides, und beides trifft sich erst hier.

    Der **unterdrückte** linke Pfeil ist ausgenommen: Er steht nur noch im DOM,
    um ausblenden zu können (siehe `suppressLeft`), und etwas Unsichtbares
    hervorzuheben wäre eine Meldung ins Leere.
  */
  const motion = useCanvasPanDirection()
  const litRaw = highlightedHint(motion?.sector ?? null, hints, motion?.dx ?? 0, motion?.dy ?? 0)
  const lit = suppressAll || (litRaw === 'left' && suppressLeft) ? null : litRaw

  // Die Grundklasse jedes Pfeils — mit `--suppressed`, wenn alle weichen.
  const hintClass = `canvas-offscreen-hint${suppressAll ? ' canvas-offscreen-hint--suppressed' : ''}`

  /** Die Klasse für den Pfeil dieser Richtung — leer, wenn er nicht leuchtet. */
  const panning = (direction: typeof litRaw) =>
    direction === lit ? ' canvas-offscreen-hint--panning' : ''

  // Der Behälter steht IMMER, auch wenn gerade kein Pfeil nötig ist: Er ist
  // die Ebene, und deren Lage im DOM ist das, was zählt (ADR 0008). Ihn
  // wegfallen zu lassen spart einen leeren, ereignislosen `div` — und macht
  // dafür die Struktur davon abhängig, wo gerade Notizen liegen.
  return (
    <div className="canvas-offscreen-hints" aria-hidden="true">
      {left && (
        // Weicht der Werkzeug-Pille aus, wenn sie an dieser Kante steht — beide
        // wollen deren Mitte.
        <span className={`${hintClass} canvas-offscreen-hint--left${toolbarSide === 'left' ? ' canvas-offscreen-hint--dodge-toolbar' : ''}${suppressLeft ? ' canvas-offscreen-hint--suppressed' : ''}${panning('left')}`}>
          <ChevronLeft size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </span>
      )}
      {right && (
        <span className={`${hintClass} canvas-offscreen-hint--right${toolbarSide === 'right' ? ' canvas-offscreen-hint--dodge-toolbar' : ''}${panning('right')}`}>
          <ChevronRight size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </span>
      )}
      {top && (
        <span className={`${hintClass} canvas-offscreen-hint--top${dodgeActions ? ' canvas-offscreen-hint--dodge-actions' : ''}${panning('top')}`}>
          <ChevronUp size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </span>
      )}
      {bottom && (
        <span className={`${hintClass} canvas-offscreen-hint--bottom${panning('bottom')}`}>
          <ChevronDown size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </span>
      )}

      {/*
        Die **Eckpfeile**. Ein Element, das auf BEIDEN Achsen draußen liegt,
        bekommt genau diesen einen Pfeil statt zweier Kantenpfeile — siehe
        `markOffscreen`.

        Dasselbe `ChevronRight` wie an den Kanten, nur gedreht (die Drehung
        steht im SCSS): Der Randpfeil ist eine Familie, und ein Chevron in der
        Ecke liest sich als dasselbe Zeichen, nur schräg.

        Sie weichen NIE aus. Die Werkzeug-Pille und das **Drag Handle** sitzen
        mittig an der linken Kante, die Aktionsgruppe füllt oben rechts nur mit
        ihrem Padding bis in die Ecke — die Ecken selbst sind frei. Das
        Ausweichen bleibt Sache der Kantenpfeile.
      */}
      {CORNERS.map(({ key, modifier }) => hints[key] && (
        <span key={modifier} className={`${hintClass} canvas-offscreen-hint--${modifier}${panning(key)}`}>
          <ChevronRight size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </span>
      ))}
    </div>
  )
}
